/**
 * Voice — one playable unit in the polyphonic voice pool.
 *
 * Each Voice owns its own AudioNodes (osc → osGain → filter → voiceGain →
 * master) and its own envelope source (ConstantSourceNode scaled by the
 * template VCA's gain param). It binds or subscribes to the template module's
 * Parameters so a knob twist on the rack simultaneously updates every voice
 * — that's the polyphony trick: parameters are shared, AudioNodes are not.
 *
 * Lifecycle:
 *   new Voice(engine, template) → AudioNodes exist, osc silent (env offset 0)
 *   noteOn(note, vel, ...)      → schedule pitch, open envelope gate
 *   noteOff(...)                → release envelope; `active` stays true until
 *                                  the release tail decays below audibility,
 *                                  at which point VoiceManager can reclaim it.
 *   dispose()                   → unbind, disconnect, stop OscillatorNode.
 */

import type { AudioEngine } from './AudioEngine';
import type { OscillatorModule, Waveform } from './modules/Oscillator';
import type { FilterModule } from './modules/Filter';
import type { VCAModule } from './modules/VCA';
import type { EnvelopeModule } from './modules/Envelope';
import type { NoiseModule } from './modules/Noise';
import { midiToHz } from './notes';
import {
  makeWavetableBuffer,
  wavetableReferenceHz,
} from './wavetable';

export interface VoiceTemplate {
  readonly osc: OscillatorModule;
  readonly filter: FilterModule;
  readonly vca: VCAModule;
  readonly env: EnvelopeModule;
  /**
   * Optional shared noise module. When present, each Voice creates its
   * own BufferSource pointing at the module's shared buffer and routes
   * it into the filter alongside the osc — so noise rides the same
   * envelope + VCA path as the tone.
   */
  readonly noise?: NoiseModule;
  /**
   * Optional shared pitch-bend signal (cents). Voices connect it to their
   * osc.detune so MIDI pitch bend / keyboard arrows affect every active
   * note uniformly. Must live for the voice's lifetime — typically a
   * ConstantSourceNode owned by main.ts.
   */
  readonly pitchBendSource?: AudioNode | null;
}

function waveformToOscType(w: Waveform): OscillatorType {
  switch (w) {
    case 'sine':
      return 'sine';
    case 'square':
      return 'square';
    case 'saw':
      return 'sawtooth';
    case 'triangle':
      return 'triangle';
    // Pulse shares the sawtooth source — Voice routes it through a
    // sign-function WaveShaper with a DC offset to synthesize the
    // variable-width pulse.
    case 'pulse':
      return 'sawtooth';
    // Capture mode disconnects the OscillatorNode entirely and uses
    // an AudioBufferSourceNode instead, so this fallback is just a
    // safe default for the (silent) OscillatorNode in that mode.
    case 'capture':
      return 'sine';
  }
}

/**
 * Sign-function curve for the pulse WaveShaper. Maps any positive
 * input to +1 and any negative to -1. Length is a small odd number
 * so the exact-zero sample lands on a curve index, where we choose
 * 0 (silent) for symmetry — the in-flight saw should never sit at
 * exactly zero for long anyway.
 */
const PULSE_CURVE = (() => {
  const N = 1025;
  const out = new Float32Array(N);
  for (let i = 0; i < N; i++) {
    const x = (i / (N - 1)) * 2 - 1;
    out[i] = x > 0 ? 1 : x < 0 ? -1 : 0;
  }
  return out;
})();

/** Sample resolution for the per-voice fold curve. Same as the
 *  FOLD effect — high enough that a fold pass doesn't introduce
 *  visible quantization. */
const FOLD_CURVE_SAMPLES = 2048;

function buildFoldCurve(fold: number): Float32Array {
  const out = new Float32Array(FOLD_CURVE_SAMPLES);
  for (let i = 0; i < FOLD_CURVE_SAMPLES; i++) {
    const x = (i / (FOLD_CURVE_SAMPLES - 1)) * 2 - 1;
    out[i] = Math.sin(Math.PI * x * fold);
  }
  return out;
}

export class Voice {
  /** MIDI note currently assigned to this voice, or -1 when free. */
  note = -1;
  /** Note velocity, 0..1. */
  velocity = 0;
  /** Context time when the current note started (for oldest-first stealing). */
  startTime = 0;
  /** True while a note is playing or releasing. */
  active = false;
  /**
   * True when the voice is in its release phase — `noteOff` has been
   * called but the envelope hasn't finished fading. Voice stealing
   * prefers releasing voices over sustaining ones because cutting a
   * fading voice is much less audibly disruptive than cutting a held
   * note.
   */
  get isReleasing(): boolean {
    return this.releaseTimer !== null;
  }

  /**
   * Extra per-voice detune offset in cents. Used by unison mode to spread
   * a stack of voices around a center pitch. Summed with the template's
   * osc.detune + coarse*100 whenever recalcDetune runs.
   */
  private voiceDetuneOffset = 0;

  private readonly osc: OscillatorNode;
  private readonly osGain: GainNode;
  private readonly filter: BiquadFilterNode;
  private readonly voiceGain: GainNode;
  private readonly envSource: ConstantSourceNode;
  private readonly envScale: GainNode;
  /**
   * Per-voice wavefold subgraph — sits between osGain and filter.
   * Mirrors the FOLD effect's internals (preGain → waveShaper +
   * dry/wet crossfade) but lives inside each Voice so each note's
   * harmonics fold independently with its own envelope. Always in
   * the path; Drive=0 collapses to fully-dry so it's audibly
   * inert when the user doesn't want folding.
   *
   *   osGain ─┬─► foldDryGain ────────────────────► foldOut → filter
   *           └─► foldPreGain ─► foldShaper ─► foldWetGain ─┘
   */
  private readonly foldOut: GainNode;
  private readonly foldDryGain: GainNode;
  private readonly foldWetGain: GainNode;
  private readonly foldPreGain: GainNode;
  private readonly foldShaper: WaveShaperNode;
  /**
   * Capture-mode subgraph — when waveform === 'capture', the
   * OscillatorNode is disconnected and a looping
   * `AudioBufferSourceNode` plays the captured wavetable into
   * `osGain` instead. Buffer-source `.buffer` and `.start()` are
   * one-shot, so the source is rebuilt every time the captured
   * wavetable changes (same pattern as `rebuildNoiseSource`).
   */
  private bufferSource: AudioBufferSourceNode | null = null;
  /** Reference frequency of the captured wavetable — drives the
   *  `playbackRate = midiToHz(note) / referenceHz` math. */
  private bufferReferenceHz: number;
  /** True when waveform === 'capture' (so the saw is disconnected
   *  from osGain and bufferSource is feeding it instead). */
  private captureActive = false;
  /**
   * Pulse-width subgraph — only wired into the audio path when the
   * waveform is `pulse`. Layout when active:
   *
   *   osc(saw) ─┐
   *             ├─►(+) pulseSum ─► pulseShaper ─► osGain
   *   widthDC ─┘
   *
   * `widthDC` is a ConstantSourceNode whose offset holds the static
   * pulse-width contribution mapped from the [0,1] Width param to
   * [-1,+1] DC. Audio-rate mod sources (e.g. an LFO routed to OSC
   * Width via the matrix) sum on top of this AudioParam — that's
   * what gives PWM its sample-by-sample shape morph. When the
   * waveform is anything other than pulse, this subgraph stays
   * connected to nothing on the input side (the saw doesn't reach
   * it) so it's silent and free.
   */
  private readonly pulseSum: GainNode;
  private readonly pulseShaper: WaveShaperNode;
  private readonly widthDC: ConstantSourceNode;
  // Noise path — parallel with osc into the filter. Source is rebuilt on
  // Color change (BufferSources are single-shot), so `noiseSource` is
  // mutable. Gain is fixed per-voice; the template's Level parameter
  // binds to every voice's gain via multi-target bindAudioParam.
  private noiseSource: AudioBufferSourceNode | null = null;
  private readonly noiseGain: GainNode | null;

  /**
   * Per-note mod-source signals. Each is a ConstantSourceNode whose offset
   * is set at noteOn time — velocity as 0..1, keytrack as a note number
   * normalized over C2..C7. The ModRouter treats them as per-voice sources
   * and wires them per-voice (one depth-gain per voice per connection).
   */
  private readonly velocityNode: ConstantSourceNode;
  private readonly keytrackNode: ConstantSourceNode;

  private readonly engine: AudioEngine;
  private readonly template: VoiceTemplate;
  private readonly unsubs: Array<() => void> = [];

  private releaseTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(engine: AudioEngine, template: VoiceTemplate) {
    this.engine = engine;
    this.template = template;
    const ctx = engine.context;
    this.bufferReferenceHz = wavetableReferenceHz(ctx.sampleRate);

    // Create per-voice AudioNodes.
    this.osc = ctx.createOscillator();
    this.osc.type = waveformToOscType(template.osc.waveform.value);
    this.osc.frequency.value = 220;
    this.osc.detune.value = this.composedDetuneCents();

    this.osGain = ctx.createGain();
    this.osGain.gain.value = template.osc.level.value;

    this.filter = ctx.createBiquadFilter();
    this.filter.type = template.filter.filterType.value;
    this.filter.frequency.value = template.filter.cutoff.value;
    this.filter.Q.value = template.filter.resonance.value;

    this.voiceGain = ctx.createGain();
    this.voiceGain.gain.value = 0; // driven by envelope via envScale

    this.envSource = ctx.createConstantSource();
    this.envSource.offset.value = 0;
    this.envScale = ctx.createGain();
    this.envScale.gain.value = template.vca.gain.value;

    this.velocityNode = ctx.createConstantSource();
    this.velocityNode.offset.value = 0;
    this.keytrackNode = ctx.createConstantSource();
    this.keytrackNode.offset.value = 0;

    // Pulse subgraph — built up-front so the routing swap is just
    // connect/disconnect calls. WaveShaper curve is the sign function
    // (precomputed once at module load); 4× oversample softens the
    // worst of the aliasing the discontinuity introduces.
    this.pulseSum = ctx.createGain();
    this.pulseSum.gain.value = 1;
    this.pulseShaper = ctx.createWaveShaper();
    this.pulseShaper.curve = PULSE_CURVE as unknown as Float32Array<ArrayBuffer>;
    this.pulseShaper.oversample = '4x';
    this.widthDC = ctx.createConstantSource();
    // Initial DC offset reflects the Width parameter's value mapped
    // from [0,1] (UI) to [-1,+1] (DC). Width=0.5 ⇒ offset=0 ⇒ square.
    this.widthDC.offset.value = template.osc.width.value * 2 - 1;
    this.widthDC.connect(this.pulseSum);
    this.pulseSum.connect(this.pulseShaper);

    // Fold subgraph — always in the path. Drive=0 means foldDryGain=1,
    // foldWetGain=0 (audibly inert). Higher Drive both increases
    // wet level and pre-amplifies the input pushing into the curve.
    this.foldPreGain = ctx.createGain();
    this.foldShaper = ctx.createWaveShaper();
    this.foldShaper.oversample = '2x';
    const initialFold = template.osc.fold.value;
    const initialDrive = template.osc.drive.value;
    this.foldShaper.curve = buildFoldCurve(initialFold) as unknown as Float32Array<ArrayBuffer>;
    this.foldDryGain = ctx.createGain();
    this.foldWetGain = ctx.createGain();
    this.foldOut = ctx.createGain();
    // osGain → fold subgraph → foldOut. Wired below in the routing
    // section that decides which source feeds osGain.
    this.osGain.connect(this.foldDryGain).connect(this.foldOut);
    this.osGain.connect(this.foldPreGain);
    this.foldPreGain.connect(this.foldShaper);
    this.foldShaper.connect(this.foldWetGain);
    this.foldWetGain.connect(this.foldOut);
    this.applyFoldDrive(initialDrive, /*instant*/ true);

    // Downstream chain. osGain feeds the fold subgraph (which
    // crossfades dry/wet by Drive), foldOut feeds the filter.
    // Only the *source* feeding osGain swaps based on waveform —
    // applyWaveformRouting handles the three cases (plain osc /
    // pulse / capture).
    this.foldOut.connect(this.filter).connect(this.voiceGain);
    this.voiceGain.connect(engine.voiceBus);
    this.applyWaveformRouting(template.osc.waveform.value);

    // Noise path: noiseSource → noiseGain → filter (parallel with osGain).
    // Sharing the downstream filter + voiceGain means noise rides the
    // same envelope and the same VCA as the tone — turning Level to 0
    // (or playing no note) silences it, and a sharp envelope chops it
    // the same way it chops the osc.
    if (template.noise) {
      this.noiseGain = ctx.createGain();
      this.noiseGain.gain.value = template.noise.level.value;
      this.noiseGain.connect(this.filter);
      template.noise.level.bindAudioParam(this.noiseGain.gain, ctx);
      this.rebuildNoiseSource();
    } else {
      this.noiseGain = null;
    }

    // Pitch-bend: a shared ConstantSourceNode (if provided) whose offset is
    // driven in cents. Summed into osc.detune on top of coarse + fine detune.
    if (template.pitchBendSource) {
      template.pitchBendSource.connect(this.osc.detune);
    }

    // Wire modulation: envSource → envScale → voiceGain.gain (AudioParam sum)
    this.envSource.connect(this.envScale).connect(this.voiceGain.gain);

    this.osc.start();
    this.envSource.start();
    this.velocityNode.start();
    this.keytrackNode.start();
    this.widthDC.start();

    // Bind simple 1:1 parameter → AudioParam mappings. Parameter.bindAudioParam
    // accepts multiple targets, so every Voice's AudioParams track the shared
    // template parameter together.
    template.osc.level.bindAudioParam(this.osGain.gain, ctx);
    template.filter.cutoff.bindAudioParam(this.filter.frequency, ctx);
    template.filter.resonance.bindAudioParam(this.filter.Q, ctx);
    template.vca.gain.bindAudioParam(this.envScale.gain, ctx);

    // Composite detune: voice.osc.detune = template.osc.detune + coarse*100
    // + voiceDetuneOffset (unison spread). We manage this ourselves via
    // subscribe; binding two Parameters to one AudioParam would fight over
    // cancelScheduledValues calls.
    this.unsubs.push(template.osc.detune.subscribe(() => this.recalcDetune()));
    this.unsubs.push(template.osc.coarse.subscribe(() => this.recalcDetune()));

    // Waveform changes — re-run the same routing logic the
    // constructor used so swapping between sine/saw/pulse/capture
    // updates the audio graph cleanly. Underlying OscillatorNode
    // type is also adjusted (it's still running in capture mode,
    // just disconnected from the chain).
    this.unsubs.push(
      template.osc.waveform.subscribe((v) => {
        this.osc.type = waveformToOscType(v);
        this.applyWaveformRouting(v);
      }),
    );

    // Capture buffer changes — when the user hits "Capture" in the
    // UI, every voice rebuilds its AudioBufferSourceNode against
    // the new wavetable. Same one-shot pattern as noise; the
    // buffer source can't be re-bound after start().
    this.unsubs.push(
      template.osc.subscribeCapture(() => {
        if (this.captureActive) this.rebuildBufferSource();
      }),
    );

    // Width → DC offset on the pulse subgraph. Maps [0,1] UI range to
    // [-1,+1] DC. Audio-rate modulation from the matrix sums into
    // widthDC.offset on top of this static contribution — exactly
    // what classic analog PWM does.
    this.unsubs.push(
      template.osc.width.subscribe((v) => {
        const dc = v * 2 - 1;
        const t = ctx.currentTime;
        this.widthDC.offset.cancelScheduledValues(t);
        this.widthDC.offset.setTargetAtTime(dc, t, 0.005);
      }),
    );

    // Drive → dry/wet crossfade + pre-gain into the folder. Listens
    // to the display channel so audio-rate mod (LFO → OSC · Drive)
    // also re-aims the crossfade in real time, not just preset writes.
    this.unsubs.push(
      template.osc.drive.subscribeDisplay((v) => this.applyFoldDrive(v, false)),
    );
    // Fold → curve shape. Rebuild the WaveShaper's lookup table when
    // the fold count changes. Cheap (2048 samples + a sin loop) and
    // happens at human knob-twist rates, not audio rate.
    this.unsubs.push(
      template.osc.fold.subscribeDisplay((v) => {
        this.foldShaper.curve = buildFoldCurve(v) as unknown as Float32Array<ArrayBuffer>;
      }),
    );

    // Noise color swap: BufferSource.buffer is immutable after start(),
    // so a color change means "stop old source, build a new one pointed
    // at the new buffer, start it".
    if (template.noise) {
      this.unsubs.push(template.noise.color.subscribe(() => this.rebuildNoiseSource()));
    }
    this.unsubs.push(
      template.filter.filterType.subscribe((v) => {
        this.filter.type = v;
      }),
    );
  }

  /**
   * Schedule a note. `retrigger` controls whether the envelope restarts from
   * its current offset (true) or continues from where it is (false, legato).
   * `glideSec` slews the oscillator pitch over that time; 0 = instant.
   */
  noteOn(
    note: number,
    velocity = 1,
    when?: number,
    glideSec = 0,
    retrigger = true,
  ): void {
    const ctx = this.engine.context;
    const t = when ?? ctx.currentTime;

    // Schedule pitch. If a previous note is still releasing, a linear ramp
    // from current value to the new Hz gives portamento for free.
    const hz = midiToHz(note);
    this.osc.frequency.cancelScheduledValues(t);
    if (glideSec > 0 && this.active) {
      const current = this.osc.frequency.value;
      this.osc.frequency.setValueAtTime(current, t);
      this.osc.frequency.linearRampToValueAtTime(hz, t + glideSec);
    } else {
      this.osc.frequency.setValueAtTime(hz, t);
    }

    // Capture-mode voices use playbackRate on a BufferSourceNode
    // for pitch — same shape as the oscillator's frequency ramp,
    // just translated through the wavetable's reference Hz.
    if (this.captureActive && this.bufferSource) {
      const targetRate = hz / this.bufferReferenceHz;
      const rateParam = this.bufferSource.playbackRate;
      rateParam.cancelScheduledValues(t);
      if (glideSec > 0 && this.active) {
        const current = rateParam.value;
        rateParam.setValueAtTime(current, t);
        rateParam.linearRampToValueAtTime(targetRate, t + glideSec);
      } else {
        rateParam.setValueAtTime(targetRate, t);
      }
    }

    // Force-apply the composed detune at note start. setVoiceDetuneOffset
    // (called by VoiceManager before noteOn) is a no-op when the offset
    // matches the voice's last value — which leaves osc.detune stale if
    // the Detune knob moved, or pitch bend scheduled, or the voice was
    // disposed/recycled in the interim. Rescheduling here keeps the
    // AudioParam in sync with template.osc.detune + coarse + offset.
    this.recalcDetune();

    // Per-note modulation sources. Velocity is 0..1 directly. Keytrack
    // normalizes the note number over the C2..C7 range (MIDI 36..96) so most
    // playable keyboards produce a near-full 0..1 sweep.
    this.velocityNode.offset.setValueAtTime(velocity, t);
    const KT_LOW = 36;
    const KT_HIGH = 96;
    const kt = Math.max(0, Math.min(1, (note - KT_LOW) / (KT_HIGH - KT_LOW)));
    this.keytrackNode.offset.setValueAtTime(kt, t);

    if (retrigger || !this.active) {
      // Full AR on the envelope: attack to velocity-scaled peak, decay to sustain.
      const a = Math.max(0.001, this.template.env.attack.value);
      const d = Math.max(0.001, this.template.env.decay.value);
      const s = Math.max(0, Math.min(1, this.template.env.sustain.value));
      const peak = velocity;
      const sustainLevel = s * velocity;
      const offset = this.envSource.offset;
      const current = offset.value;
      offset.cancelScheduledValues(t);
      offset.setValueAtTime(current, t);
      offset.linearRampToValueAtTime(peak, t + a);
      offset.setTargetAtTime(sustainLevel, t + a, d / 3);
    }

    this.note = note;
    this.velocity = velocity;
    this.startTime = t;
    this.active = true;
    if (this.releaseTimer) {
      clearTimeout(this.releaseTimer);
      this.releaseTimer = null;
    }
  }

  /** Begin the release phase of the envelope; voice stays `active` until it fades. */
  noteOff(when?: number): void {
    const ctx = this.engine.context;
    const t = when ?? ctx.currentTime;
    const r = Math.max(0.001, this.template.env.release.value);

    const offset = this.envSource.offset;
    const current = offset.value;
    offset.cancelScheduledValues(t);
    offset.setValueAtTime(current, t);
    offset.setTargetAtTime(0, t, r / 3);

    // Mark the voice free once the release has essentially decayed (~4×
    // time-constant is ~98% of the way to zero).
    if (this.releaseTimer) clearTimeout(this.releaseTimer);
    const settleMs = r * 1000 + 50;
    this.releaseTimer = setTimeout(() => {
      this.active = false;
      this.note = -1;
      this.releaseTimer = null;
    }, settleMs);
  }

  /**
   * Look up a modulation-target AudioParam on this voice.
   *
   * Mod connections address the *template* module + the parameter's name
   * (e.g. `filter.id, 'Cutoff'`); the router resolves that to the actual
   * per-voice AudioParam here. Returns null if the address doesn't map to a
   * modulatable param on this voice — the router silently skips those.
   *
   * Phase 4 exposes Level / Cutoff / Q / Gain as the modulatable set.
   * osc.Detune is deliberately excluded because its AudioParam is already
   * driven by manual composite scheduling (coarse + detune); a mod source
   * would fight those writes. Future phases split that out properly.
   */
  /**
   * Look up a per-voice modulation source by sentinel id. Used by the
   * ModRouter to wire per-note connections. Returns null for unknown ids.
   */
  getModSourceNode(sourceId: string): AudioNode | null {
    if (sourceId === 'velocity') return this.velocityNode;
    if (sourceId === 'keytrack') return this.keytrackNode;
    // The raw envelope signal — ramps 0 → velocity at attack, decays
    // toward velocity·sustain, drops back to 0 on release. Routing
    // it to OSC.Coarse with positive depth gives a per-note pitch
    // sweep (fart-style "ppthhhpt" — pitch starts high then drops as
    // the envelope settles).
    if (sourceId === 'envelope') return this.envSource;
    return null;
  }

  getAudioParam(moduleId: string, paramName: string): AudioParam | null {
    if (moduleId === this.template.osc.id) {
      if (paramName === 'Level') return this.osGain.gain;
      // osc.detune is already being driven by the shared pitch-bend
      // source — audio-rate mods sum additively at the AudioParam, so
      // LFO → OSC · Detune does what the user expects alongside bend.
      if (paramName === 'Detune') return this.osc.detune;
      // Pulse Width — modulating widthDC.offset is exactly classic
      // PWM. Inactive when the waveform isn't 'pulse', but exposing
      // the AudioParam unconditionally is fine: the matrix writes
      // sum harmlessly into the silent subgraph.
      if (paramName === 'Width') return this.widthDC.offset;
      // Drive and Fold — Drive is exposed via foldPreGain.gain (the
      // input boost) so audio-rate mod sources push harder into the
      // curve sample-by-sample. Note: this only modulates the
      // pre-gain side, not the dry/wet crossfade — the crossfade is
      // a smooth function of the param value driven via subscribeDisplay,
      // which already responds to mod writes through the display channel.
      // For Fold there's no AudioParam (the curve is a static lookup
      // table that we rebuild on knob twist), so it's matrix-driven
      // through the display channel only — return null here so the
      // router falls back to the param-write path for that target.
      if (paramName === 'Drive') return this.foldPreGain.gain;
    } else if (moduleId === this.template.filter.id) {
      if (paramName === 'Cutoff') return this.filter.frequency;
      if (paramName === 'Q') return this.filter.Q;
    } else if (moduleId === this.template.vca.id) {
      if (paramName === 'Gain') return this.envScale.gain;
    }
    return null;
  }

  /** Hard cut — cancels envelope ramps, zeroes output immediately. */
  silence(): void {
    if (this.releaseTimer) {
      clearTimeout(this.releaseTimer);
      this.releaseTimer = null;
    }
    const t = this.engine.context.currentTime;
    this.envSource.offset.cancelScheduledValues(t);
    this.envSource.offset.setValueAtTime(0, t);
    this.active = false;
    this.note = -1;
    // Reset unison detune offset so a subsequent re-use starts from a clean
    // slate — otherwise a panic'd voice could carry a stale spread value
    // into its next note if the VoiceManager's setVoiceDetuneOffset early-
    // returns (same-value optimization).
    this.voiceDetuneOffset = 0;
    this.osc.detune.cancelScheduledValues(t);
    this.osc.detune.setTargetAtTime(this.composedDetuneCents(), t, 0.005);
  }

  dispose(): void {
    this.silence();
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;

    const ctx = this.engine.context;
    this.template.osc.level.unbindAudioParam(this.osGain.gain);
    this.template.filter.cutoff.unbindAudioParam(this.filter.frequency);
    this.template.filter.resonance.unbindAudioParam(this.filter.Q);
    this.template.vca.gain.unbindAudioParam(this.envScale.gain);
    if (this.noiseGain && this.template.noise) {
      this.template.noise.level.unbindAudioParam(this.noiseGain.gain);
    }
    void ctx; // keep for possible future use (e.g. scheduled disconnects)

    try {
      this.osc.stop();
    } catch {
      /* already stopped */
    }
    try {
      this.envSource.stop();
    } catch {
      /* already stopped */
    }
    if (this.noiseSource) {
      try {
        this.noiseSource.stop();
      } catch {
        /* already stopped */
      }
      this.noiseSource.disconnect();
      this.noiseSource = null;
    }
    if (this.bufferSource) {
      try {
        this.bufferSource.stop();
      } catch {
        /* already stopped */
      }
      this.bufferSource.disconnect();
      this.bufferSource = null;
    }
    try {
      this.velocityNode.stop();
    } catch {
      /* already stopped */
    }
    try {
      this.keytrackNode.stop();
    } catch {
      /* already stopped */
    }
    try {
      this.widthDC.stop();
    } catch {
      /* already stopped */
    }
    this.osc.disconnect();
    this.osGain.disconnect();
    this.filter.disconnect();
    this.voiceGain.disconnect();
    this.envSource.disconnect();
    this.envScale.disconnect();
    this.velocityNode.disconnect();
    this.keytrackNode.disconnect();
    this.pulseSum.disconnect();
    this.pulseShaper.disconnect();
    this.widthDC.disconnect();
    this.foldDryGain.disconnect();
    this.foldWetGain.disconnect();
    this.foldPreGain.disconnect();
    this.foldShaper.disconnect();
    this.foldOut.disconnect();
  }

  /**
   * Set this voice's private detune offset (cents). Used by VoiceManager's
   * unison allocation so each voice in a stack plays slightly off from the
   * center pitch. Offsets are additive with the template's detune + coarse.
   */
  setVoiceDetuneOffset(cents: number): void {
    if (cents === this.voiceDetuneOffset) return;
    this.voiceDetuneOffset = cents;
    this.recalcDetune();
  }

  /**
   * Wire the active source for the given waveform into `osGain`.
   * Disconnects whatever was previously feeding osGain (saw,
   * pulseShaper output, or buffer source) and connects the new
   * one. Called once at construction and whenever the Wave
   * parameter changes. Idempotent on the same waveform.
   */
  private applyWaveformRouting(wave: Waveform): void {
    const wantPulse = wave === 'pulse';
    const wantCapture = wave === 'capture';

    // Tear down all current connections feeding osGain so we can
    // re-establish exactly one path. Wrapped in try/catch since
    // disconnect throws on already-disconnected nodes.
    try {
      this.osc.disconnect();
    } catch {
      /* no-op */
    }
    try {
      this.pulseShaper.disconnect();
    } catch {
      /* no-op */
    }
    if (this.bufferSource) {
      try {
        this.bufferSource.disconnect();
      } catch {
        /* no-op */
      }
    }

    // Pitch bend re-attaches to osc.detune — disconnect() severs
    // upstream connections, so we reconnect after every swap. Cheap.
    if (this.template.pitchBendSource) {
      this.template.pitchBendSource.connect(this.osc.detune);
    }

    if (wantCapture) {
      // Build a fresh BufferSourceNode from the current wavetable
      // (or silence if none captured yet). The source feeds osGain
      // until the next routing swap.
      this.captureActive = true;
      this.rebuildBufferSource();
    } else if (wantPulse) {
      this.captureActive = false;
      this.osc.connect(this.pulseSum);
      this.pulseShaper.connect(this.osGain);
    } else {
      this.captureActive = false;
      this.osc.connect(this.osGain);
    }
  }

  /**
   * Build or rebuild the AudioBufferSourceNode that plays the
   * captured wavetable. Called when the user hits Capture (the
   * OscillatorModule fires `subscribeCapture`) and when the
   * waveform switches into 'capture' mode. The buffer is one-cycle;
   * `loop = true` repeats it forever, and `playbackRate` controls
   * pitch.
   */
  private rebuildBufferSource(): void {
    if (!this.captureActive) return;
    const ctx = this.engine.context;

    if (this.bufferSource) {
      try {
        this.bufferSource.stop();
      } catch {
        /* already stopped */
      }
      try {
        this.bufferSource.disconnect();
      } catch {
        /* already disconnected */
      }
      this.bufferSource = null;
    }

    const wavetable = this.template.osc.getCapturedWavetable();
    if (!wavetable) {
      // No wave captured yet — leave the source un-built so the
      // voice is silent in capture mode. A subsequent capture will
      // wire it up via the subscribeCapture path.
      return;
    }

    const buffer = makeWavetableBuffer(ctx, wavetable);
    const src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    // Pitch tracking — the wavetable plays at `referenceHz` when
    // playbackRate=1; scale to the current note. recalcDetune
    // handles fine-detune via .detune in cents on top.
    const targetHz = this.note >= 0 ? midiToHz(this.note) : 220;
    src.playbackRate.value = targetHz / this.bufferReferenceHz;
    src.detune.value = this.composedDetuneCents();
    if (this.template.pitchBendSource) {
      try {
        this.template.pitchBendSource.connect(src.detune);
      } catch {
        /* no-op */
      }
    }
    src.connect(this.osGain);
    try {
      src.start();
    } catch {
      /* shim or double-start */
    }
    this.bufferSource = src;
  }

  /**
   * Build or rebuild this voice's noise BufferSource. Called at construction
   * and again whenever Color changes (BufferSource.buffer is immutable
   * after start()). Fails quietly if the NoiseModule hasn't generated its
   * buffers yet — can happen at voice-pool init before engine.unlock.
   */
  private rebuildNoiseSource(): void {
    if (!this.template.noise || !this.noiseGain) return;
    const buffer = this.template.noise.getBuffer(this.template.noise.color.value);
    if (!buffer) return;
    const ctx = this.engine.context;

    if (this.noiseSource) {
      try {
        this.noiseSource.stop();
      } catch {
        /* already stopped */
      }
      this.noiseSource.disconnect();
    }
    this.noiseSource = ctx.createBufferSource();
    this.noiseSource.buffer = buffer;
    this.noiseSource.loop = true;
    this.noiseSource.connect(this.noiseGain);
    try {
      this.noiseSource.start();
    } catch {
      /* shim or double-start */
    }
  }

  /**
   * Apply a Drive value (0..1) to the fold subgraph's three gain
   * stages. Equal-power crossfade between dry and wet (cos/sin),
   * plus a pre-gain that pushes harder into the curve as Drive
   * climbs. Mirrors the FOLD effect's mapping so the per-voice
   * folder behaves the same way at the same knob position.
   *
   *   foldDryGain  = cos(d · π/2)        (full at d=0, zero at d=1)
   *   foldWetGain  = sin(d · π/2)        (zero at d=0, full at d=1)
   *   foldPreGain  = 1 + d · 5           (1×..6× input boost)
   *
   * The `instant` flag is used during construction to set values
   * synchronously without scheduling; runtime updates use a short
   * setTargetAtTime ramp so a knob twist or audio-rate mod write
   * doesn't zipper.
   */
  private applyFoldDrive(drive: number, instant = false): void {
    const d = Math.max(0, Math.min(1, drive));
    const dry = Math.cos((d * Math.PI) / 2);
    const wet = Math.sin((d * Math.PI) / 2);
    const pre = 1 + d * 5;
    if (instant) {
      this.foldDryGain.gain.value = dry;
      this.foldWetGain.gain.value = wet;
      this.foldPreGain.gain.value = pre;
      return;
    }
    const ctx = this.engine.context;
    const t = ctx.currentTime;
    const tau = 0.01;
    this.foldDryGain.gain.cancelScheduledValues(t);
    this.foldDryGain.gain.setTargetAtTime(dry, t, tau);
    this.foldWetGain.gain.cancelScheduledValues(t);
    this.foldWetGain.gain.setTargetAtTime(wet, t, tau);
    this.foldPreGain.gain.cancelScheduledValues(t);
    this.foldPreGain.gain.setTargetAtTime(pre, t, tau);
  }

  private recalcDetune(): void {
    const ctx = this.engine.context;
    const cents = this.composedDetuneCents();
    const t = ctx.currentTime;
    this.osc.detune.cancelScheduledValues(t);
    this.osc.detune.setTargetAtTime(cents, t, 0.005);
  }

  private composedDetuneCents(): number {
    // Round Coarse to whole semitones — the Parameter stores a continuous
    // value (knob drags land at non-integer positions) but the formatter
    // shows "+0" even at e.g. 0.4st, so users set "Detune=0, Coarse=0"
    // and still hear ~40 cents of pitch offset. Snapping here keeps the
    // displayed "+0" honest without forcing the Parameter to discretize.
    return (
      this.template.osc.detune.value +
      Math.round(this.template.osc.coarse.value) * 100 +
      this.voiceDetuneOffset
    );
  }
}
